import { cn } from '@/utils/cn'
import type { SuggestionChip } from '@/types'

export interface SuggestionChipsProps {
  chips: SuggestionChip[]
  onSelect: (text: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Tappable follow-up prompts shown beneath an assistant message. Selecting a
 * chip sends its text as the next message. The handoff chip is styled distinctly
 * so the route to a care advocate reads as the deliberate "next step".
 */
export function SuggestionChips({
  chips,
  onSelect,
  disabled = false,
  className,
}: SuggestionChipsProps) {
  if (chips.length === 0) return null

  return (
    <div
      className={cn('mt-2 flex flex-wrap gap-2', className)}
      role="group"
      aria-label="Suggested follow-ups"
    >
      {chips.map((chip, idx) => {
        const isHandoff = chip.kind === 'handoff'
        return (
          <button
            key={`${idx}-${chip.text}`}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(chip.text)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isHandoff
                ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500'
                : 'border-primary-200 bg-white text-primary-700 hover:bg-primary-50 focus:ring-primary-500',
            )}
          >
            {chip.text}
          </button>
        )
      })}
    </div>
  )
}
